import { useEffect, useState } from 'react';
import {
  PlayButton,
  SelectSpeakerButton,
  SpeakersTableContainer,
} from './styles';
import { Speaker, SpeakerFilters } from '../';
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Spinner,
  useTheme,
} from '@fluentui/react';
import { MagicWand, Play } from '@phosphor-icons/react';

interface SpeakersTableProps {
  speakers: Speaker[] | undefined;
  filters: SpeakerFilters | undefined;
  searchTerm: string;
  preview: boolean;
  onSelect: (speaker: Speaker) => void;
  onPlaySample: (speaker: Speaker) => void;
}

export const SpeakersTable = ({
  speakers,
  filters,
  preview,
  onSelect,
  onPlaySample,
  searchTerm,
}: SpeakersTableProps) => {
  const columnsConfig = {
    isRowHeader: true,
    isResizable: true,
    isSortedDescending: false,
    sortAscendingAriaLabel: 'Sorted A to Z',
    sortDescendingAriaLabel: 'Sorted Z to A',
    maxWidth: 300,
  };

  const initialColumns: IColumn[] = [
    {
      ...columnsConfig,
      key: 'speaker-sample' + crypto.randomUUID(),
      name: '',
      fieldName: 'sample',
      minWidth: 30,
      maxWidth: 30,
      isResizable: false,
      data: 'string',
      onRender: (item: Speaker) => {
        return (
          <>
            {item.sampleaudio && (
              <PlayButton
                onClick={() => {
                  onPlaySample(item);
                }}
              >
                <Play size={20} weight="duotone" />
              </PlayButton>
            )}
          </>
        );
      },
    },
    {
      ...columnsConfig,
      key: 'speaker-name' + crypto.randomUUID(),
      name: 'Name',
      fieldName: 'key',
      minWidth: 150,
      maxWidth: 150,
      isSorted: true,
    },
    {
      ...columnsConfig,
      key: 'speaker-language' + crypto.randomUUID(),
      name: 'Language',
      fieldName: 'locale',
      minWidth: 200,
      isSorted: true,
    },
    {
      ...columnsConfig,
      key: 'speaker-styles' + crypto.randomUUID(),
      name: 'Style',
      fieldName: 'styles',
      minWidth: 100,
      isSorted: true,
      onRender: (item: Speaker) => {
        return (
          <>
            {item.styles && (
              <MagicWand
                weight="duotone"
                alt={item.styles?.join(', ')}
                style={{ cursor: 'pointer' }}
              />
            )}
          </>
        );
      },
    },
    {
      ...columnsConfig,
      key: 'speaker-gender' + crypto.randomUUID(),
      name: 'Gender',
      fieldName: 'gender',
      minWidth: 100,
      isSorted: true,
    },
    {
      ...columnsConfig,
      key: 'speaker-type' + crypto.randomUUID(),
      name: 'Type',
      fieldName: 'type',
      minWidth: 100,
      isSorted: true,
    },
    {
      ...columnsConfig,
      key: 'speaker-selection' + crypto.randomUUID(),
      name: '',
      fieldName: 'select',
      minWidth: 80,
      maxWidth: 80,
      isResizable: false,
      data: 'string',
      onRender: (item: Speaker) => {
        return (
          <SelectSpeakerButton
            onClick={() => {
              onSelect(item);
            }}
          >
            Select
          </SelectSpeakerButton>
        );
      },
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>(initialColumns);
  const [filteredItems, setFilteredItems] = useState<Speaker[]>([]);
  const [isCompactMode, setIsCompactMode] = useState<boolean>(false);
  const theme = useTheme();

  const sortItems = (column: IColumn): void => {
    const sortedItems = [...filteredItems];

    sortedItems.sort((a, b) => {
      const aValue = a[column.fieldName as keyof Speaker];
      const bValue = b[column.fieldName as keyof Speaker];

      if (Array.isArray(aValue) && Array.isArray(bValue)) {
        const aString = aValue.join(' ');
        const bString = bValue.join(' ');
        return column.isSortedDescending
          ? bString.localeCompare(aString)
          : aString.localeCompare(bString);
      }

      return column.isSortedDescending
        ? String(bValue).localeCompare(String(aValue))
        : String(aValue).localeCompare(String(bValue));
    });

    setFilteredItems(sortedItems);

    const newColumns = columns.map((col) => {
      if (col.key === column.key) {
        return {
          ...col,
          isSorted: true,
          isSortedDescending: !col.isSortedDescending,
        };
      }
      return { ...col, isSorted: false, isSortedDescending: false };
    });

    setColumns(newColumns);
  };

  const onColumnClick = (
    ev: React.MouseEvent<HTMLElement>,
    column: IColumn
  ): void => {
    sortItems(column);
  };

  useEffect(() => {
    let newItems = speakers ?? [];

    if (filters) {
      newItems.forEach((item) => {
        if (filters.gender) {
          newItems = newItems.filter((item) => filters.gender === item.gender);
        }
        if (filters.type) {
          newItems = newItems.filter((item) => filters.type === item.type);
        }
        if (filters.language) {
          newItems = newItems.filter(
            (item) => filters.language === item.locale
          );
        }

        if (!preview) {
          newItems = newItems.filter((item) => item.status === 'GA');
        }
      });
    }

    if (searchTerm) {
      newItems = newItems.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.locale.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredItems(newItems);
  }, [filters, speakers, preview, searchTerm]);

  return (
    <SpeakersTableContainer>
      {!filteredItems ? (
        <Spinner size={3} label="Loading items" />
      ) : (
        <DetailsList
          items={filteredItems}
          compact={isCompactMode}
          columns={columns}
          selectionMode={SelectionMode.none}
          getKey={(item: Speaker) => item.key}
          setKey="none"
          layoutMode={DetailsListLayoutMode.justified}
          isHeaderVisible={true}
          theme={theme}
          onColumnHeaderClick={(ev?, column?) =>
            onColumnClick(
              ev as React.MouseEvent<HTMLElement>,
              column as IColumn
            )
          }
        />
      )}
    </SpeakersTableContainer>
  );
};
